import type { interpretationElasticityWidgetSchema } from '@/entities/optimization-statistic'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { TFunction } from 'i18next'
import type z from 'zod'

import { useTranslation } from 'react-i18next'

import ChartViewRecharts from './ChartViewRecharts'

export default function Elasticity({
	data: { demand_curve, init_price, optimal_price, relative_elasticity },
}: {
	data: z.infer<typeof interpretationElasticityWidgetSchema>
}) {
	const { t } = useTranslation()

	const data = getData(demand_curve)
	const chartConfig = getChartConfig(t)
	const plotLines = getPlotLines(init_price, optimal_price, t)

	return (
		<div className='flex flex-col gap-4'>
			<div className='mt-2'>
				<span className='text-sm'>{t('general_price_elasticity')}</span>
				{' - '}
				<strong className='font-bold capitalize'>
					{t(relative_elasticity || '', { defaultValue: relative_elasticity })}
				</strong>
			</div>
			<ChartViewRecharts
				data={data}
				chartConfig={chartConfig}
				plotLines={plotLines}
			/>
		</div>
	)
}

const getData = (
	demandCurve: z.infer<
		typeof interpretationElasticityWidgetSchema
	>['demand_curve']
) => {
	const items = demandCurve.price.length || 0
	const data = []
	for (let i = 0; i < items; i++) {
		data.push({
			price: demandCurve.price[i],
			sales_items: demandCurve.sales_items[i],
			revenue: demandCurve.revenue[i],
			gross_profit: demandCurve.gross_profit[i],
		})
	}

	return data.sort((a, b) => a.price - b.price)
}
const getChartConfig = (t: TFunction) => {
	const chartConfig = {
		sales_items: {
			label: t('sales_items'),
			color: '#E87B35',
		},
		revenue: {
			label: t('revenue'),
			color: '#00B557',
		},
		gross_profit: {
			label: t('gross_profit'),
			color: '#F44236',
		},
	} satisfies ChartConfig

	return chartConfig
}
const getPlotLines = (
	initPrice: number,
	optimalPrice: number,
	t: TFunction
) => {
	const isPriceChanged = initPrice !== optimalPrice

	return isPriceChanged
		? [
				{
					color: '#32B2F3',
					label: t('init_price'),
					value: initPrice,
				},
				{
					color: '#7378ED',
					label: t('optimal_price'),
					value: optimalPrice,
				},
			]
		: [
				{
					color: '#32B2F3',
					label: t('init_price'),
					value: initPrice,
				},
			]
}
